import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Chip, CircularProgress, MenuItem, Stack, Tab, Tabs, Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar, ComposedChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip as RTooltip, Legend, ReferenceLine,
} from "recharts";
import { CardHeader, GlassCard, T, TYPE, CHART, SERIES, ChartTooltip, formatDate, labelSx } from "../../user/complaints/shared";
import { pm, errorMessage } from "../api";
import { usePm } from "../PmContext";
import { StatusPill, Assignee } from "../ui";
import { fieldSx } from "../inputs";
import { Burndown } from "../project/Sprints";
import { ReportView } from "../project/ProjectReports";

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const Loading = () => <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress size={22} /></Box>;
const StatBox = ({ label, value, unit = "" }) => (
  <GlassCard sx={{ p: 1.5 }}><Typography sx={labelSx}>{label}</Typography><Typography sx={{ fontWeight: 700, fontSize: "1.25rem" }}>{value ?? "—"}{value !== null && value !== undefined ? unit : ""}</Typography></GlassCard>
);

const useMetric = (board, name, params) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const key = JSON.stringify(params);
  useEffect(() => {
    let alive = true; // ignore responses to a previous date range / filter
    setData(null);
    setError("");
    pm.boardMetric(board._id, name, params).then((d) => alive && setData(d)).catch((err) => alive && setError(errorMessage(err)));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board._id, name, key]);
  return { data, error };
};

// ---------------------------------------------------------------------------
const Cfd = ({ board, params }) => {
  const { data, error } = useMetric(board, "cumulative-flow", params);
  if (error) return <Alert severity="info">{error}</Alert>;
  if (!data) return <Loading />;
  // stacked bottom-up in reverse column order so Done sits at the bottom, like Jira
  const order = [...data.columns].reverse();
  return (
    <GlassCard>
      <CardHeader title="Cumulative flow" subtitle={`Issues per column at the end of each day, rebuilt from status history (${data.timezone})${data.truncated ? " · limited to the first 5000 issues" : ""}`} />
      <Box sx={{ height: 360, p: 2 }}>
        <ResponsiveContainer>
          <AreaChart data={data.series}>
            <CartesianGrid stroke={CHART.grid} vertical={false} />
            <XAxis dataKey="day" tick={CHART.tick} {...CHART.axisProps} tickFormatter={(d) => d.slice(5)} />
            <YAxis tick={CHART.tick} {...CHART.axisProps} width={34} allowDecimals={false} />
            <RTooltip content={<ChartTooltip />} />
            <Legend />
            {order.map((c, i) => <Area key={c} type="monotone" dataKey={c} stackId="1" stroke={SERIES[(order.length - 1 - i) % SERIES.length]} fill={SERIES[(order.length - 1 - i) % SERIES.length]} fillOpacity={0.55} />)}
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </GlassCard>
  );
};

const TimeStats = ({ board, params, name, title, field }) => {
  const { data, error } = useMetric(board, name, params);
  const buckets = useMemo(() => {
    if (!data) return [];
    const m = new Map();
    for (const i of data.issues) {
      if (i[field] === null) continue;
      const b = Math.floor(i[field]);
      m.set(b, (m.get(b) || 0) + 1);
    }
    return [...m.entries()].sort((a, b) => a[0] - b[0]).map(([days, issues]) => ({ days: `${days}d`, issues }));
  }, [data, field]);
  if (error) return <Alert severity="info">{error}</Alert>;
  if (!data) return <Loading />;
  const s = data.stats;
  return (
    <Stack spacing={2}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" }, gap: 1.5 }}>
        <StatBox label="Completed" value={s.count} />
        <StatBox label="Average" value={s.mean} unit=" d" />
        <StatBox label="Median" value={s.median} unit=" d" />
        <StatBox label="85th percentile" value={s.p85} unit=" d" />
        <StatBox label="Longest" value={s.max} unit=" d" />
      </Box>
      {name === "cycle-time" && data.withoutStart > 0 && <Alert severity="info">{data.withoutStart} issue(s) went straight to done without an in-progress status and have no cycle time.</Alert>}
      <GlassCard>
        <CardHeader title={title} subtitle={`${data.calendar === "working" ? "Working" : "Calendar"} days · distribution`} />
        <Box sx={{ height: 240, p: 2 }}>
          <ResponsiveContainer>
            <BarChart data={buckets}><CartesianGrid stroke={CHART.grid} vertical={false} /><XAxis dataKey="days" tick={CHART.tick} {...CHART.axisProps} /><YAxis tick={CHART.tick} {...CHART.axisProps} width={30} allowDecimals={false} /><RTooltip content={<ChartTooltip />} /><Bar dataKey="issues" fill={SERIES[0]} radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </Box>
        <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
          <Table size="small">
            <TableHead><TableRow>{["Issue", "Started", "Completed", "Days"].map((h) => <TableCell key={h} sx={labelSx}>{h}</TableCell>)}</TableRow></TableHead>
            <TableBody>{[...data.issues].reverse().map((i) => <TableRow key={i._id}><TableCell sx={{ fontSize: TYPE.small }}><b>{i.key}</b> {i.summary}</TableCell><TableCell sx={{ fontSize: TYPE.micro }}>{i.startedAt ? formatDate(i.startedAt) : "—"}</TableCell><TableCell sx={{ fontSize: TYPE.micro }}>{formatDate(i.completedAt)}</TableCell><TableCell>{i[field] ?? "—"}</TableCell></TableRow>)}</TableBody>
          </Table>
        </Box>
      </GlassCard>
    </Stack>
  );
};

const Throughput = ({ board, params }) => {
  const [interval, setInterval] = useState("week");
  const { data, error } = useMetric(board, "throughput", { ...params, interval });
  if (error) return <Alert severity="info">{error}</Alert>;
  return (
    <GlassCard>
      <CardHeader title="Throughput" subtitle={data ? `${data.total} completed · average ${data.average} per ${data.interval}` : ""}
        action={<ToggleButtonGroup size="small" exclusive value={interval} onChange={(_, v) => v && setInterval(v)}>{["day", "week", "month"].map((v) => <ToggleButton key={v} value={v} sx={{ textTransform: "none", fontSize: TYPE.micro }}>{v}</ToggleButton>)}</ToggleButtonGroup>} />
      {!data ? <Loading /> : (
        <Box sx={{ height: 280, p: 2 }}>
          <ResponsiveContainer>
            <BarChart data={data.series}><CartesianGrid stroke={CHART.grid} vertical={false} /><XAxis dataKey="period" tick={CHART.tick} {...CHART.axisProps} /><YAxis tick={CHART.tick} {...CHART.axisProps} width={30} allowDecimals={false} /><RTooltip content={<ChartTooltip />} /><ReferenceLine y={data.average} stroke={T.inkMuted} strokeDasharray="4 4" /><Bar dataKey="issues" name="Completed" fill={SERIES[2]} radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </GlassCard>
  );
};

const Wip = ({ board, params }) => {
  const { data, error } = useMetric(board, "wip", params);
  if (error) return <Alert severity="info">{error}</Alert>;
  if (!data) return <Loading />;
  return (
    <Stack spacing={2}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.5 }}>
        <StatBox label="Current WIP" value={data.current} />
        <StatBox label="Average WIP" value={data.average} />
        <StatBox label="Maximum WIP" value={data.max} />
        <StatBox label="Limit violations" value={`${data.violationDays} days · ${data.recordedViolations} moves`} />
      </Box>
      <GlassCard>
        <CardHeader title="Work in progress over time" subtitle="Issues in columns that hold an in-progress status" />
        <Box sx={{ height: 240, p: 2 }}>
          <ResponsiveContainer>
            <LineChart data={data.series}><CartesianGrid stroke={CHART.grid} vertical={false} /><XAxis dataKey="day" tick={CHART.tick} {...CHART.axisProps} tickFormatter={(d) => d.slice(5)} /><YAxis tick={CHART.tick} {...CHART.axisProps} width={30} allowDecimals={false} /><RTooltip content={<ChartTooltip />} /><Line dataKey="wip" name="WIP" stroke={SERIES[0]} strokeWidth={2} dot={false} /></LineChart>
          </ResponsiveContainer>
        </Box>
        <Table size="small">
          <TableHead><TableRow>{["Column", "WIP limit", "Current", "Average", "Max", "Days over limit"].map((h) => <TableCell key={h} sx={labelSx}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>{data.columns.map((c) => <TableRow key={c.columnId}><TableCell>{c.name}</TableCell><TableCell>{c.wipLimit || "—"}</TableCell><TableCell sx={{ color: c.wipLimit && c.current > c.wipLimit ? "#B42318" : T.ink }}>{c.current}</TableCell><TableCell>{c.average}</TableCell><TableCell>{c.max}</TableCell><TableCell>{c.violationDays}</TableCell></TableRow>)}</TableBody>
        </Table>
      </GlassCard>
    </Stack>
  );
};

const Control = ({ board, params }) => {
  const { data, error } = useMetric(board, "control-chart", params);
  if (error) return <Alert severity="info">{error}</Alert>;
  if (!data) return <Loading />;
  const pts = data.points.map((p) => ({ ...p, t: new Date(p.completedAt).getTime() }));
  const normal = pts.filter((p) => !p.outlier);
  const outliers = pts.filter((p) => p.outlier);
  return (
    <Stack spacing={2}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" }, gap: 1.5 }}>
        <StatBox label="Issues" value={data.stats.count} />
        <StatBox label="Average cycle" value={data.stats.mean} unit=" d" />
        <StatBox label="Median" value={data.stats.median} unit=" d" />
        <StatBox label="Std deviation" value={data.stats.stddev} unit=" d" />
        <StatBox label="Outliers" value={data.outliers} />
      </Box>
      <GlassCard>
        <CardHeader title="Control chart" subtitle={`Cycle time per completed issue · rolling average over ${data.window} issues · outliers are more than 2σ above the mean`} />
        <Box sx={{ height: 360, p: 2 }}>
          {!pts.length ? <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>No completed issues in this range.</Typography> : (
            <ResponsiveContainer>
              <ComposedChart data={pts}>
                <CartesianGrid stroke={CHART.grid} />
                <XAxis dataKey="t" type="number" domain={["dataMin", "dataMax"]} tick={CHART.tick} {...CHART.axisProps} tickFormatter={(t) => new Date(t).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} />
                <YAxis tick={CHART.tick} {...CHART.axisProps} width={34} label={{ value: "days", angle: -90, position: "insideLeft", fontSize: 11 }} />
                <ZAxis range={[40, 40]} />
                <RTooltip formatter={(v, n) => [v, n]} labelFormatter={(t) => new Date(t).toLocaleDateString("en-IN")} />
                <Legend />
                {data.stats.mean !== null && <ReferenceLine y={data.stats.mean} stroke={T.inkMuted} strokeDasharray="4 4" label={{ value: "average", fontSize: 10, fill: T.inkMuted }} />}
                <Scatter name="Issues" data={normal} dataKey="cycleDays" fill={SERIES[0]} />
                <Scatter name="Outliers" data={outliers} dataKey="cycleDays" fill={SERIES[4]} />
                <Line name="Rolling average" dataKey="rolling" stroke={SERIES[2]} dot={false} strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Box>
      </GlassCard>
    </Stack>
  );
};

const Aging = ({ board, params }) => {
  const { data, error } = useMetric(board, "aging-items", params);
  if (error) return <Alert severity="info">{error}</Alert>;
  if (!data) return <Loading />;
  return (
    <GlassCard>
      <CardHeader title="Aging work items" subtitle={`Open issues by time in their current status · ${data.stale} over threshold (board default ${data.defaultThreshold} days; columns can override in Board settings)`} />
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead><TableRow>{["Issue", "Column", "Status", "Assignee", "In status", "Age", "Threshold"].map((h) => <TableCell key={h} sx={labelSx}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {!data.rows.length && <TableRow><TableCell colSpan={7} sx={{ color: T.inkMuted, textAlign: "center" }}>No open work.</TableCell></TableRow>}
            {data.rows.map((r) => (
              <TableRow key={r._id} sx={{ bgcolor: r.stale ? "#FEF3F2" : undefined }}>
                <TableCell sx={{ fontSize: TYPE.small }}><b>{r.key}</b> {r.summary}</TableCell>
                <TableCell sx={{ fontSize: TYPE.small }}>{r.column || "—"}</TableCell>
                <TableCell><StatusPill statusId={r.statusId} /></TableCell>
                <TableCell><Assignee person={r.assignee} size={20} showName /></TableCell>
                <TableCell sx={{ fontWeight: 700, color: r.stale ? "#B42318" : T.ink }}>{r.daysInStatus} days</TableCell>
                <TableCell sx={{ fontSize: TYPE.small }}>{r.ageDays} days</TableCell>
                <TableCell sx={{ fontSize: TYPE.small }}>{r.threshold} days</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </GlassCard>
  );
};

// ---------------------------------------------------------------------------
const Burnup = ({ sprint }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    pm.burnup(sprint._id).then(setData).catch(() => setData({ series: [] }));
  }, [sprint._id]);
  return (
    <GlassCard>
      <CardHeader title="Burnup" subtitle="Completed work vs total scope — scope changes stay visible" />
      <Box sx={{ height: 280, p: 2 }}>
        {!data ? <CircularProgress size={20} /> : (
          <ResponsiveContainer>
            <LineChart data={data.series}><CartesianGrid stroke={CHART.grid} vertical={false} /><XAxis dataKey="day" tick={CHART.tick} {...CHART.axisProps} tickFormatter={(d) => d.slice(5)} /><YAxis tick={CHART.tick} {...CHART.axisProps} width={34} /><RTooltip content={<ChartTooltip />} /><Legend /><Line dataKey="scope" name="Total scope" stroke={SERIES[1]} strokeWidth={2} dot={false} /><Line dataKey="completed" name="Completed" stroke={SERIES[2]} strokeWidth={2.5} dot={{ r: 2 }} /></LineChart>
          </ResponsiveContainer>
        )}
      </Box>
    </GlassCard>
  );
};

const BoardVelocity = ({ board }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    pm.boardVelocity(board._id).then(setData).catch(() => setData(null));
  }, [board._id]);
  if (!data) return <Loading />;
  return (
    <GlassCard>
      <CardHeader title="Velocity" subtitle={data.note} action={data.averageCompleted !== null && <Chip size="small" label={`Average ${data.averageCompleted} ${data.unit}`} />} />
      <Box sx={{ height: 280, p: 2 }}>
        {!data.sprints.length ? <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>Complete a sprint on this board to see velocity.</Typography> : (
          <ResponsiveContainer>
            <BarChart data={data.sprints}><CartesianGrid stroke={CHART.grid} vertical={false} /><XAxis dataKey="name" tick={CHART.tick} {...CHART.axisProps} /><YAxis tick={CHART.tick} {...CHART.axisProps} width={34} /><RTooltip content={<ChartTooltip />} /><Legend /><Bar dataKey="committed" name="Committed" fill={SERIES[1]} radius={[4, 4, 0, 0]} /><Bar dataKey="completed" name="Completed" fill={SERIES[2]} radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </GlassCard>
  );
};

const SCRUM_TABS = [["burndown", "Burndown"], ["burnup", "Burnup"], ["velocity", "Velocity"], ["sprint", "Sprint report"], ["cfd", "Cumulative flow"], ["control", "Control chart"]];
const KANBAN_TABS = [["cfd", "Cumulative flow"], ["cycle", "Cycle time"], ["lead", "Lead time"], ["throughput", "Throughput"], ["wip", "WIP"], ["control", "Control chart"], ["aging", "Aging work"]];

const BoardReports = ({ board, initial }) => {
  const ctx = usePm();
  const tabs = board.type === "scrum" ? SCRUM_TABS : KANBAN_TABS;
  const [tab, setTab] = useState(tabs.some(([k]) => k === initial) ? initial : tabs[0][0]);
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(today());
  const [typeId, setTypeId] = useState("");
  const [calendar, setCalendar] = useState("calendar");
  const [sprints, setSprints] = useState([]);
  const [sprintId, setSprintId] = useState("");
  useEffect(() => {
    if (tabs.some(([k]) => k === initial)) setTab(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);
  useEffect(() => {
    if (board.type !== "scrum") return;
    pm.boardSprints(board._id, { status: "active,completed" }).then((s) => {
      setSprints(s);
      setSprintId((cur) => cur || s.find((x) => x.status === "active")?._id || s.find((x) => x.status === "completed")?._id || "");
    }).catch(() => {});
  }, [board._id, board.type]);
  const sprint = sprints.find((s) => s._id === sprintId);
  const params = { from, to, ...(typeId ? { typeId } : {}), calendar };
  const sprintTab = ["burndown", "burnup", "sprint"].includes(tab);
  return (
    <Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ mb: 2, minHeight: 36, "& .MuiTab-root": { minHeight: 36, textTransform: "none", fontSize: TYPE.small } }}>
        {tabs.map(([k, l]) => <Tab key={k} value={k} label={l} />)}
      </Tabs>
      <Stack direction="row" spacing={1.25} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap alignItems="center">
        {sprintTab ? (
          <TextField select size="small" label="Sprint" value={sprintId} onChange={(e) => setSprintId(e.target.value)} sx={{ ...fieldSx, minWidth: 200 }}>
            {sprints.map((s) => <MenuItem key={s._id} value={s._id}>{s.name} ({s.status})</MenuItem>)}
          </TextField>
        ) : tab !== "velocity" && (
          <>
            {tab !== "aging" && <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} sx={fieldSx} />}
            {tab !== "aging" && <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} sx={fieldSx} />}
            <TextField select size="small" label="Issue type" value={typeId} onChange={(e) => setTypeId(e.target.value)} sx={{ ...fieldSx, minWidth: 140 }}>
              <MenuItem value="">All types</MenuItem>{ctx.issueTypes.map((t) => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
            </TextField>
            {["cycle", "lead", "control"].includes(tab) && (
              <ToggleButtonGroup size="small" exclusive value={calendar} onChange={(_, v) => v && setCalendar(v)}>
                <ToggleButton value="calendar" sx={{ textTransform: "none", fontSize: TYPE.micro }}>Calendar days</ToggleButton>
                <ToggleButton value="working" sx={{ textTransform: "none", fontSize: TYPE.micro }}>Working days</ToggleButton>
              </ToggleButtonGroup>
            )}
          </>
        )}
      </Stack>
      {sprintTab && !sprint ? <Alert severity="info">Start a sprint on this board to see sprint reports.</Alert> : (
        <>
          {tab === "burndown" && <Burndown sprint={sprint} unitDefault={board.estimationConfig?.unit === "count" ? "count" : ["originalEstimate", "remainingEstimate", "hours", "days"].includes(board.estimationConfig?.unit) ? "hours" : "points"} />}
          {tab === "burnup" && <Burnup sprint={sprint} />}
          {tab === "sprint" && <ReportView name="sprint" params={{ projectId: board.project._id, sprintId }} project={board.project} />}
          {tab === "velocity" && <BoardVelocity board={board} />}
          {tab === "cfd" && <Cfd board={board} params={params} />}
          {tab === "cycle" && <TimeStats board={board} params={params} name="cycle-time" title="Cycle time (in progress → done)" field="cycleDays" />}
          {tab === "lead" && <TimeStats board={board} params={params} name="lead-time" title="Lead time (created → done)" field="leadDays" />}
          {tab === "throughput" && <Throughput board={board} params={params} />}
          {tab === "wip" && <Wip board={board} params={params} />}
          {tab === "control" && <Control board={board} params={params} />}
          {tab === "aging" && <Aging board={board} params={typeId ? { typeId } : {}} />}
        </>
      )}
    </Box>
  );
};

export default BoardReports;
